import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import {
  hasSubdivisions,
  isKnownSubdivision,
  subdivisionFilterFor,
  subdivisionOptionsFor,
  type SubdivisionOption,
} from "@/shared/data/subdivisions";

export type SubdivisionFieldState = {
  /** ISO 3166-1 alpha-3 currently selected in the governing country field, when it is one. */
  country: string | undefined;
  /** Options for that country. Empty when the country has no catalog — the caller must then take free text. */
  options: readonly SubdivisionOption[];
  /** Prefix-per-word filter covering the aliases; `undefined` when there is nothing to filter. */
  filter: ((itemValue: string, query: string) => boolean) | undefined;
  /** Whether the field can be offered as a list at all. */
  isSelectable: boolean;
};

/**
 * Keeps a subdivision field in sync with the country field that scopes it.
 *
 * Same shape as `useCountryScopedDocumentType`, with one deliberate difference: when the current value stops
 * being valid it is **cleared**, never replaced by the first option. A document type has a sensible default;
 * a province does not, and auto-selecting the alphabetically first one would silently submit an address the
 * user never typed.
 *
 * Reads the value with `getValues` rather than `watch` so the effect does not re-run on every keystroke of
 * the field it is writing to.
 */
export function useSubdivisionField(name: string, countryName: string): SubdivisionFieldState {
  const { watch, getValues, setValue, clearErrors } = useFormContext();

  const watched = watch(countryName);
  const country = typeof watched === "string" ? watched : undefined;

  const isSelectable = hasSubdivisions(country);
  const options = subdivisionOptionsFor(country);
  const filter = subdivisionFilterFor(country);

  // The country the last reconciliation saw. Seeded with the mounting country, so the first pass compares a
  // country against itself and only ever judges the value already in the form.
  const reconciledAgainst = useRef(country);

  useEffect(() => {
    const previous = reconciledAgainst.current;
    reconciledAgainst.current = country;

    const current = getValues(name);
    if (typeof current !== "string" || current === "") return;

    if (hasSubdivisions(country)) {
      // The new country has a catalog, so the question is answerable: keep what belongs to it, drop what
      // does not. A value shared by both countries (e.g. Amazonas in COL, PER and BRA) survives.
      if (isKnownSubdivision(country, current)) return;
    } else if (!hasSubdivisions(previous)) {
      // Free text before and free text now: nothing here can judge what the user typed, so it stands.
      return;
    }
    // Otherwise the country moved OUT of a catalog into free text. The value cannot be anything but a
    // subdivision of the country that no longer applies, so it goes with it — leaving it would keep, say,
    // a Colombian department sitting in an address that is no longer Colombian.

    setValue(name, "", { shouldValidate: false });
    // The message belonged to the value that just went away; leaving it would pin an error under a field
    // that is now merely empty.
    clearErrors(name);
  }, [country, name, getValues, setValue, clearErrors]);

  return { country, options, filter, isSelectable };
}
